import { useEffect, useRef } from 'react'
import Konva from 'konva'
import { Circle, Group, Text } from 'react-konva'
import { ClosedShape } from './entityPositions'
import { MAX_ENTITY_COUNT } from '../constants'
import { ResolvedEntityBadge } from './entityAssignmentMatching'
import { fitEntityLabelFontSize } from './entityBadgeFont'
import { getEntityMarkerPlacement } from './entityMarkerLayout'
import { resolveCanvasSubscriptionGoldColor } from '../canvasClassColor'

type Props = {
  shape: ClosedShape
  width: number
  height: number
  bodyNode: Konva.Node | null
  zoomScale?: number
  startNumber?: number
  entityAssignments?: Map<number, ResolvedEntityBadge>
  showText?: boolean
}

const ShapeEntityMarkers = ({
  shape,
  width,
  height,
  bodyNode,
  zoomScale = 1,
  startNumber = 1,
  entityAssignments,
  showText = true,
}: Props) => {
  const count = Math.min(shape.entity_count ?? 0, MAX_ENTITY_COUNT)
  const groupRef = useRef<Konva.Group>(null)
  const scale = zoomScale > 1 ? zoomScale : 1

  const placement = getEntityMarkerPlacement(shape, width, height)
  const { markerRadius, fontSize, positions } = placement

  useEffect(() => {
    if (!bodyNode || count <= 0) return

    const sync = () => {
      const group = groupRef.current
      if (!group) return
      group.x(bodyNode.x())
      group.y(bodyNode.y())
      group.rotation(bodyNode.rotation())
      const upright = -bodyNode.rotation()
      group.getChildren().forEach((child) => {
        child.rotation(upright)
      })
      group.getLayer()?.batchDraw()
    }

    sync()
    bodyNode.on('dragmove.entityMarkers transform.entityMarkers', sync)
    return () => {
      bodyNode.off('.entityMarkers')
    }
  }, [bodyNode, count, positions.length])

  if (count <= 0) return null

  const entityLabelWidth = markerRadius * 2 * 1.4
  const assignedNames = positions
    .map((_, i) => entityAssignments?.get(startNumber + i)?.name)
    .filter((name): name is string => Boolean(name))
  const sharedEntityFontSize =
    assignedNames.length > 0
      ? Math.min(
          ...assignedNames.map((name) =>
            fitEntityLabelFontSize(
              name,
              entityLabelWidth,
              11 / scale,
              fontSize,
            ),
          ),
        )
      : fontSize

  return (
    <Group
      ref={groupRef}
      x={placement.centerX}
      y={placement.centerY}
      rotation={placement.rotation}
      listening={false}
    >
      {positions.map((pos, i) => {
        const entityBadge = entityAssignments?.get(startNumber + i)
        const label = entityBadge ? entityBadge.name : String(startNumber + i)
        const isOwn = entityBadge?.isOwn ?? false
        const borderColor = isOwn ? resolveCanvasSubscriptionGoldColor() : ''
        const labelColor = entityBadge?.color ?? 'rgba(248, 250, 252, 0.95)'
        const labelWidth = entityBadge ? entityLabelWidth : markerRadius * 2
        const labelFontSize = entityBadge ? sharedEntityFontSize : fontSize
        return (
          <Group
            key={`${shape.id}-entity-${i}`}
            x={pos.x}
            y={pos.y}
            rotation={-placement.rotation}
          >
            <Circle
              radius={markerRadius}
              fill='rgba(15, 23, 42, 0.85)'
              stroke={isOwn ? borderColor : 'rgba(248, 250, 252, 0.9)'}
              strokeWidth={isOwn ? 3 / scale : 1 / scale}
            />
            {showText ? (
              <Text
                x={-labelWidth / 2}
                y={-markerRadius}
                width={labelWidth}
                height={markerRadius * 2}
                text={label}
                fontSize={labelFontSize}
                fontStyle='bold'
                fill={labelColor}
                align='center'
                verticalAlign='middle'
                wrap='none'
                shadowColor='rgba(0, 0, 0, 0.9)'
                shadowBlur={4}
                shadowOpacity={1}
              />
            ) : null}
          </Group>
        )
      })}
    </Group>
  )
}

export default ShapeEntityMarkers
